import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Icons, iconSize, palette, Stack, Typography } from '@munifocus/ui';
import { useFileHandovers } from '../../lib/teamwork-api';
import { formatPeriod } from './handover-display';
import { HandoverStatusChip } from './HandoverStatusChip';
import { WriteHandoverDialog } from './WriteHandoverDialog';

/**
 * The shift handovers that name this file: who handed it to whom, for when,
 * and whether it was acknowledged — plus the way to write one about it.
 */
export function FileHandoversCard({
  file,
}: {
  file: { id: string; reference: string; address: string };
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const handovers = useFileHandovers(file.id);
  const [open, setOpen] = useState(false);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Icons.ArrowRightLeft size={iconSize.md} aria-hidden />
          <Typography variant="h6">{t('gov.teamwork.handovers.fileTitle')}</Typography>
        </Stack>
        <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
          {t('gov.teamwork.handovers.write')}
        </Button>
      </Stack>
      {handovers.data?.length === 0 && (
        <Typography variant="body2" sx={{ color: palette.text.muted }}>
          {t('gov.teamwork.handovers.fileEmpty')}
        </Typography>
      )}
      {handovers.data?.map((h) => (
        <Box
          key={h.id}
          component="button"
          type="button"
          onClick={() => navigate(`/handovers/${h.id}`)}
          sx={{
            appearance: 'none',
            textAlign: 'left',
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            background: 'none',
            border: `1px solid ${palette.border.line}`,
            borderRadius: 1.5,
            p: 1.5,
            width: '100%',
          }}
        >
          <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t('gov.teamwork.handovers.fromTo', { from: h.from.name, to: h.to.name })}
            </Typography>
            <HandoverStatusChip handover={h} />
          </Stack>
          <Typography variant="caption" sx={{ color: palette.text.muted }}>
            {formatPeriod(h.periodStart, h.periodEnd, i18n.language)}
          </Typography>
          {h.files.find((f) => f.fileId === file.id)?.note && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {h.files.find((f) => f.fileId === file.id)?.note}
            </Typography>
          )}
        </Box>
      ))}
      <WriteHandoverDialog open={open} onClose={() => setOpen(false)} initialFiles={[file]} />
    </Stack>
  );
}
